import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { qk } from '../../../lib/queryKeys';
import { getBenchRun } from '../../../lib/bench';
import { WipeAuthorizationPanel } from '../../../components/bench/WipeAuthorizationPanel';
import { CosmeticGradePanel } from '../../../components/bench/CosmeticGradePanel';
import { RunTimeline } from '../../../components/bench/RunTimeline';
import { ReuseAcknowledgeBanner } from '../../../components/bench/ReuseAcknowledgeBanner';

/**
 * One bench run: stages, the binding outcome and applied band per device,
 * sanitization certificates, wipe authorization (admin) and cosmetic grade.
 * While the run is running the page polls every 5 s so the technician sees
 * stage results arrive. "Applied band" can differ from the computed band:
 * the condition precedence trigger keeps a higher-priority existing grade
 * (for example a manual override), and the page says so.
 */
export const Route = createFileRoute('/bench/runs/$runId')({
  component: BenchRunRoute,
});

function BenchRunRoute() {
  const { runId } = Route.useParams();
  return <BenchRunView runId={runId} />;
}

const ATTESTATION_COPY = {
  registered_bench: { tone: 'green' as const, label: 'Registered bench' },
  self_reported: { tone: 'amber' as const, label: 'Self-reported' },
};

/** A stage name from the profile can be missing when the version no longer carries it. */
function removedTestName(stageName: string | null, stageIndex: number): string {
  return stageName && stageName !== '' ? stageName : `Unnamed test (stage ${stageIndex})`;
}

export function BenchRunView({ runId }: { runId: string }) {
  const run = useQuery({
    queryKey: qk.bench.run(runId),
    queryFn: () => getBenchRun(runId),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 5_000 : false),
  });

  if (run.isPending) return <LoadingShell rows={8} />;
  if (run.isError) return <ErrorState error={run.error} onRetry={() => void run.refetch()} />;
  const r = run.data;
  const attestation = ATTESTATION_COPY[r.attestationLevel];
  const partial = r.scope === 'partial';
  const removedTests = r.deselections ?? [];
  const deviceByKey = new Map(r.devices.map((d) => [d.deviceKey, d]));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bench run"
        subtitle={`${r.productName ?? 'Unknown model'} - ${r.chassisSerial ?? 'no chassis serial'}`}
        actions={
          <div className="flex gap-2">
            {partial && (
              <StatusPill tone="amber" title="An org admin removed optional tests at approval.">
                Partial scope
              </StatusPill>
            )}
            <StatusPill tone={attestation.tone}>{attestation.label}</StatusPill>
            <StatusPill>{r.status}</StatusPill>
          </div>
        }
      />

      {r.wipedNotCertified && (
        <div role="alert" className="flex gap-3 rounded-md border border-status-red/40 bg-status-red/10 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-status-red" aria-hidden />
          <div className="space-y-1 text-sm">
            <p className="font-semibold text-status-red">Wiped, not certified</p>
            <p>
              A drive on this device was wiped, but the run did not commit, so no sanitization certificate exists for
              that wipe. Boot the device again and run the wipe again to get a certificate.
            </p>
          </div>
        </div>
      )}

      <ReuseAcknowledgeBanner run={r} />

      {r.attestationLevel === 'self_reported' && (
        <p className="text-sm text-muted-foreground">
          This device was paired from an IP that is not a registered bench site. Its grade is recorded as
          self-reported and ranks below telemetry.
        </p>
      )}

      <Card className="space-y-3">
        <PanelTitle title="Devices" />
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Kind</th>
              <th className="py-1">Serial</th>
              <th className="py-1">Model</th>
              <th className="py-1">Binding</th>
              <th className="py-1">Grade</th>
            </tr>
          </thead>
          <tbody>
            {r.devices.map((d) => (
              <tr key={d.deviceIndex} className="border-t border-border">
                <td className="py-1">{d.kind}</td>
                <td className="py-1 font-mono break-all">{d.serial ?? '-'}</td>
                <td className="py-1 break-all">{d.model ?? '-'}</td>
                <td className="py-1">{d.outcome ?? (r.status === 'committed' ? '-' : 'Pending commit')}</td>
                <td className="py-1">
                  {d.appliedBand ? <ConditionBadge value={d.appliedBand} /> : '-'}
                  {d.partialScope === true && (
                    <StatusPill tone="amber" className="ml-2">
                      Partial scope
                    </StatusPill>
                  )}
                  {/* A kept older grade can be Like new while this run's own tests were partial: the band is not this run's result. */}
                  {d.partialScope === true && d.appliedBand === 'like_new' && (
                    <span className="mt-1 block text-xs text-muted-foreground">
                      Like new is an earlier grade kept from a higher-priority source. This run tested a partial scope
                      {d.computedBand ? <> and graded <ConditionBadge value={d.computedBand} /></> : null}.
                    </span>
                  )}
                  {d.conditionApplied === false && !(d.partialScope === true && d.appliedBand === 'like_new') && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Kept the existing grade (higher-priority source).
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {partial && (
        <Card className="space-y-3">
          <PanelTitle
            title="Partial scope"
            description="An org admin removed these optional tests at approval. Devices with a removed test, and the machine, grade at most Normal wear."
          />
          {removedTests.length === 0 && <p className="text-sm text-muted-foreground">The removed tests are not listed.</p>}
          <ul className="space-y-1 text-sm">
            {removedTests.map((t) => {
              const dev = deviceByKey.get(t.deviceKey);
              return (
                <li key={`${t.deviceKey}|${t.profileId}|${t.version}|${t.stageIndex}`} className="flex flex-wrap items-center gap-2">
                  <span className="font-mono">{removedTestName(t.stageName, t.stageIndex)}</span>
                  <span className="text-muted-foreground">
                    {dev ? `${dev.kind} ${dev.model ?? ''} ${dev.serial ?? ''}`.trim() : 'device not in this run'}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <Card className="space-y-3">
        <PanelTitle title="Stages" />
        {r.stages.length === 0 && <p className="text-sm text-muted-foreground">No stage results yet.</p>}
        <ul className="space-y-1 text-sm">
          {r.stages.map((s) => (
            <li key={s.seq} className="flex items-center gap-3">
              <StatusPill tone={s.status === 'pass' ? 'green' : s.status === 'skipped' ? 'neutral' : 'red'}>
                {s.status}
              </StatusPill>
              <span className="font-mono">{s.stageName}</span>
              {s.destructive && <StatusPill tone="amber">wipe</StatusPill>}
              <span className="text-muted-foreground">device {s.deviceIndex}</span>
            </li>
          ))}
        </ul>
      </Card>

      <RunTimeline run={r} />

      <WipeAuthorizationPanel run={r} />

      <Card className="space-y-3">
        <PanelTitle title="Sanitization certificates" />
        {r.certificates.length === 0 && (
          <p className="text-sm text-muted-foreground">Certificates are issued when the run commits.</p>
        )}
        <ul className="space-y-2 text-sm">
          {r.certificates.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{c.deviceSerial}</span>
              <StatusPill tone="neutral">{c.nistMethod}</StatusPill>
              <span>{c.technique}</span>
              <StatusPill tone={c.passed && c.verificationPassed ? 'green' : 'red'}>
                {c.passed && c.verificationPassed ? 'Passed' : 'Failed'}
              </StatusPill>
              {c.attestationLevel === 'self_reported' && <StatusPill tone="amber">Self-reported</StatusPill>}
            </li>
          ))}
        </ul>
      </Card>

      <CosmeticGradePanel run={r} />
    </div>
  );
}
